import React from "react";
import { Button, Input, InputNumber, Select, Switch } from "antd";
import { DeleteOutlined, ExperimentOutlined, UploadOutlined } from "@ant-design/icons";
import Panel from "../../../../components/panel/Panel";
import V2Tag from "../../../../components/v2/V2Tag";
import Loader from "../../../../components/loader/Loader";
import TreeIllustration from "../../../../assets/images/tree-illustration.png";
import useToast from "../../../../hooks/useToast";
import { ToastSkin } from "../../../../context/ToastContext";
import "./automation-editor-panel.css";

export type CooldownUnit = "minutes" | "hours" | "days";

export const COOLDOWN_PRESETS: Array<{ key: string; label: string; seconds: number }> = [
    { key: "15m", label: "15m", seconds: 15 * 60 },
    { key: "30m", label: "30m", seconds: 30 * 60 },
    { key: "1h", label: "1h", seconds: 60 * 60 },
    { key: "6h", label: "6h", seconds: 6 * 60 * 60 },
    { key: "24h", label: "24h", seconds: 24 * 60 * 60 },
    { key: "7d", label: "7d", seconds: 7 * 24 * 60 * 60 },
];

type Props = {
    open: boolean;
    loading: boolean;
    saving: boolean;
    savingIndicator: boolean;
    isNewAutomation: boolean;
    publishDisabled: boolean;
    selectedAutomationId: number | null;
    automationName: string;
    automationDescription: string;
    automationActive: 0 | 1;
    draftExitConditionId: number | null;
    triggerMaxActiveRuns: number;
    cooldownMode: string;
    cooldownCustomValue: number;
    cooldownCustomUnit: CooldownUnit;
    onClose: () => void;
    onAutomationNameChange: (v: string) => void;
    onAutomationDescriptionChange: (v: string) => void;
    onAutomationActiveChange: (v: 0 | 1) => void;
    onSelectCooldownPreset: (mode: string, seconds: number) => void;
    onSelectCustomCooldown: () => void;
    onCooldownCustomValueChange: (v: number) => void;
    onCooldownCustomUnitChange: (v: CooldownUnit) => void;
    onTriggerMaxActiveRunsChange: (v: number) => void;
    onOpenTree: () => void;
    onOpenTestLab: () => void;
    onOpenExitCondition: () => void;
    onDelete: () => Promise<void>;
    onSave: () => Promise<any>;
    onPublish: () => Promise<void>;
};

export default function AutomationEditorPanel(props: Props) {
    const {
        open,
        loading,
        saving,
        savingIndicator,
        isNewAutomation,
        publishDisabled,
        selectedAutomationId,
        automationName,
        automationDescription,
        automationActive,
        draftExitConditionId,
        triggerMaxActiveRuns,
        cooldownMode,
        cooldownCustomValue,
        cooldownCustomUnit,
        onClose,
        onAutomationNameChange,
        onAutomationDescriptionChange,
        onAutomationActiveChange,
        onSelectCooldownPreset,
        onSelectCustomCooldown,
        onCooldownCustomValueChange,
        onCooldownCustomUnitChange,
        onTriggerMaxActiveRunsChange,
        onOpenTree,
        onOpenTestLab,
        onOpenExitCondition,
        onDelete,
        onSave,
        onPublish,
    } = props;
    const { showToast, closeToast } = useToast();

    const confirmDelete = () => {
        showToast({
            skin: ToastSkin.danger,
            text: "Delete automation? This will delete this automation.",
            buttons: [
                {
                    text: "Delete",
                    onClick: async () => {
                        closeToast();
                        await onDelete();
                    },
                },
                { text: "Cancel", onClick: closeToast },
            ],
        });
    };

    const confirmPublish = () => {
        showToast({
            skin: ToastSkin.neutral,
            text: "Publishing will update the production automation. The current draft will become the live version.",
            buttons: [
                {
                    text: "Publish",
                    onClick: async () => {
                        closeToast();
                        await onPublish();
                    },
                },
                { text: "Cancel", onClick: closeToast },
            ],
        });
    };

    return (
        <Panel isOpen={open} onClose={onClose} width={560}>
            {loading ? (
                <div className="v2aep-loading">
                    <Loader size={96} />
                </div>
            ) : (
                <div className="v2aep-root">
                    <div className="v2aep-head-row">
                        <div className="v2aep-title">{automationName || "New Automation"}</div>
                        <Button type="primary" icon={<UploadOutlined />} onClick={confirmPublish} loading={saving} disabled={publishDisabled}>
                            Publish
                        </Button>
                    </div>
                    <div className="v2aep-meta-row">
                        <div className="v2aep-activity-row">
                            <Switch
                                checked={automationActive === 1}
                                onChange={(checked) => onAutomationActiveChange(checked ? 1 : 0)}
                            />
                            <div className="v2aep-status">{automationActive === 1 ? "Active" : "Inactive"}</div>
                        </div>
                        {savingIndicator ? (
                            <div className="v2aep-saving-indicator">
                                <Loader size={18} />
                                <span>Saving...</span>
                            </div>
                        ) : <div />}
                    </div>

                    <div className="v2aep-sections">
                        <div className="v2-automation-panel-section">
                            <div className="v2aep-form-grid">
                                <Input
                                    value={automationName}
                                    onChange={(e) => onAutomationNameChange(e.target.value)}
                                    placeholder="Automation title"
                                />
                                <Input.TextArea
                                    value={automationDescription}
                                    onChange={(e) => onAutomationDescriptionChange(e.target.value)}
                                    placeholder="Description"
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                />
                            </div>
                        </div>

                        <div className="v2-automation-panel-section">
                            <div className="v2-automation-section-inner">
                                <div className="v2-automation-section-title-item">Automation tree</div>
                                <div className="v2-automation-section-description">
                                    Open the visual flow editor to define the automation logic and transitions.
                                </div>
                                <button
                                    type="button"
                                    className="v2-automation-tree-illustration-btn"
                                    onClick={onOpenTree}
                                >
                                    <img src={TreeIllustration} alt="Edit automation tree" className="v2-automation-tree-illustration" />
                                    <span className="v2-automation-tree-illustration-label">Click to edit the automation tree</span>
                                </button>
                            </div>
                        </div>

                        <div className="v2-automation-panel-section">
                            <div className="v2-automation-section-inner">
                                <div className="v2-automation-section-title-item">Cooldown</div>
                                <div className="v2-automation-section-description">
                                    Prevents this automation from starting again for the same client until the cooldown window passes.
                                </div>
                                <div className="v2aep-cooldown-presets">
                                    {COOLDOWN_PRESETS.map((preset) => (
                                        <Button
                                            key={`cooldown-${preset.key}`}
                                            size="small"
                                            type={cooldownMode === preset.key ? "primary" : "default"}
                                            onClick={() => onSelectCooldownPreset(preset.key, preset.seconds)}
                                        >
                                            {preset.label}
                                        </Button>
                                    ))}
                                    <Button
                                        size="small"
                                        type={cooldownMode === "custom" ? "primary" : "default"}
                                        onClick={onSelectCustomCooldown}
                                    >
                                        Custom
                                    </Button>
                                </div>
                                {cooldownMode === "custom" ? (
                                    <div className="v2aep-cooldown-custom-row">
                                        <InputNumber
                                            min={0}
                                            value={cooldownCustomValue}
                                            onChange={(v) => onCooldownCustomValueChange(Math.max(0, Number(v || 0)))}
                                            className="v2aep-input-number-full"
                                        />
                                        <Select
                                            value={cooldownCustomUnit}
                                            onChange={(v) => onCooldownCustomUnitChange(v as CooldownUnit)}
                                            options={[
                                                { value: "minutes", label: "Minutes" },
                                                { value: "hours", label: "Hours" },
                                                { value: "days", label: "Days" },
                                            ]}
                                            className="v2aep-cooldown-unit-select"
                                            getPopupContainer={(trigger) => trigger.parentElement || document.body}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="v2-automation-panel-section">
                            <div className="v2-automation-section-inner">
                                <div className="v2-automation-section-title-item">Max active / client</div>
                                <div className="v2-automation-section-description">
                                    Limits how many runs of this automation can be active at the same time for a single client.
                                </div>
                                <InputNumber
                                    min={1}
                                    value={triggerMaxActiveRuns}
                                    onChange={(v) => onTriggerMaxActiveRunsChange(Math.max(1, Number(v || 1)))}
                                    addonBefore="Max active / client"
                                    className="v2aep-input-number-full"
                                />
                            </div>
                        </div>

                        <div className="v2-automation-panel-section">
                            <div className="v2-automation-section-inner">
                                <div className="v2-automation-section-title-item">Exit condition</div>
                                <div className="v2-automation-section-description">
                                    Exit condition stops an active run when its rules are met, even if the flow has more steps.
                                </div>
                                <div className="v2aep-exit-actions">
                                    <V2Tag color={draftExitConditionId ? "blue" : "default"}>
                                        Exit condition: {draftExitConditionId ? "configured" : "not set"}
                                    </V2Tag>
                                    <Button size="small" onClick={onOpenExitCondition} disabled={!selectedAutomationId}>
                                        {draftExitConditionId ? "Edit exit condition" : "Add exit condition"}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="v2aep-action-row">
                            <div className="v2aep-action-left">
                                {isNewAutomation ? (
                                    <Button onClick={onSave} loading={saving}>
                                        Save
                                    </Button>
                                ) : null}
                                <Button type="primary" icon={<UploadOutlined />} onClick={confirmPublish} loading={saving} disabled={publishDisabled}>
                                    Publish
                                </Button>
                                {!isNewAutomation ? (
                                    <Button
                                        className="v2aep-test-lab-btn"
                                        icon={<ExperimentOutlined />}
                                        onClick={onOpenTestLab}
                                        disabled={!selectedAutomationId}
                                    >
                                        Test Lab
                                    </Button>
                                ) : null}
                            </div>
                            {!isNewAutomation ? (
                                <Button type="primary" danger icon={<DeleteOutlined />} onClick={confirmDelete} loading={saving}>
                                    Delete
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
}
